/**
 * Canadian Financial Institution Numbers
 * Source: Payments Canada Financial Institutions File (FIF)
 * 
 * Institution numbers are 3-digit codes assigned by Payments Canada
 * Transit numbers are 5-digit branch identifiers
 */

export interface CanadianBank {
  institutionNumber: string;
  name: string;
  shortName: string;
}

/**
 * List of major Canadian financial institutions
 * Covers ~95%+ of banking transactions in Canada
 */
export const CANADIAN_BANKS: CanadianBank[] = [
  { institutionNumber: '001', name: 'Bank of Montreal', shortName: 'BMO' },
  { institutionNumber: '002', name: 'Scotiabank (Bank of Nova Scotia)', shortName: 'Scotiabank' },
  { institutionNumber: '003', name: 'Royal Bank of Canada', shortName: 'RBC' },
  { institutionNumber: '004', name: 'Toronto-Dominion Bank', shortName: 'TD' },
  { institutionNumber: '006', name: 'National Bank of Canada', shortName: 'National Bank' },
  { institutionNumber: '010', name: 'Canadian Imperial Bank of Commerce', shortName: 'CIBC' },
  { institutionNumber: '016', name: 'HSBC Bank Canada', shortName: 'HSBC' },
  { institutionNumber: '030', name: 'Canadian Western Bank', shortName: 'CWB' },
  { institutionNumber: '039', name: 'Laurentian Bank of Canada', shortName: 'Laurentian' },
  { institutionNumber: '117', name: 'Government of Canada', shortName: 'Gov Canada' },
  { institutionNumber: '219', name: 'ATB Financial', shortName: 'ATB' },
  { institutionNumber: '241', name: 'Bank of America Canada', shortName: 'Bank of America' },
  { institutionNumber: '260', name: 'Citibank Canada', shortName: 'Citibank' },
  { institutionNumber: '290', name: 'JP Morgan Chase Bank', shortName: 'JP Morgan' },
  { institutionNumber: '308', name: 'Bank of China (Canada)', shortName: 'Bank of China' },
  { institutionNumber: '309', name: 'Citizens Bank of Canada', shortName: 'Citizens Bank' },
  { institutionNumber: '310', name: 'First Nations Bank of Canada', shortName: 'First Nations Bank' },
  { institutionNumber: '315', name: 'Canadian Tire Bank', shortName: 'Canadian Tire' },
  { institutionNumber: '320', name: 'President\'s Choice Financial', shortName: 'PC Financial' },
  { institutionNumber: '326', name: 'Simplii Financial', shortName: 'Simplii' },
  { institutionNumber: '338', name: 'Canadian Tire Bank', shortName: 'CT Bank' },
  { institutionNumber: '540', name: 'Manulife Bank of Canada', shortName: 'Manulife Bank' },
  { institutionNumber: '614', name: 'Tangerine Bank', shortName: 'Tangerine' },
  { institutionNumber: '621', name: 'Equitable Bank', shortName: 'Equitable' },
  { institutionNumber: '809', name: 'Credit Union Central of British Columbia', shortName: 'CUCBC' },
  { institutionNumber: '815', name: 'Desjardins (Caisses Populaires)', shortName: 'Desjardins' },
  { institutionNumber: '828', name: 'Credit Union Central of Ontario', shortName: 'CUCO' },
  { institutionNumber: '829', name: 'Credit Union Central of Manitoba', shortName: 'CUCM' },
  { institutionNumber: '837', name: 'Meridian Credit Union', shortName: 'Meridian' },
  { institutionNumber: '839', name: 'Heritage Credit Union', shortName: 'Heritage CU' },
  { institutionNumber: '842', name: 'Envision Financial', shortName: 'Envision' },
  { institutionNumber: '865', name: 'Coastal Community Credit Union', shortName: 'Coastal Community' },
  { institutionNumber: '879', name: 'Credit Union Central of Saskatchewan', shortName: 'CUCS' },
  { institutionNumber: '889', name: 'Credit Union Central Alberta', shortName: 'CUCA' },
  { institutionNumber: '899', name: 'Credit Union Central of Canada', shortName: 'CUCC' }
];

/**
 * Lookup map for quick bank name retrieval by institution number
 */
export const BANK_BY_INSTITUTION: Record<string, CanadianBank> = CANADIAN_BANKS.reduce(
  (acc, bank) => {
    acc[bank.institutionNumber] = bank;
    return acc;
  },
  {} as Record<string, CanadianBank>
);

/**
 * Get bank details by institution number
 * @param institutionNumber - 3-digit institution number
 * @returns Bank details or undefined if not found
 */
export const getBankByInstitution = (institutionNumber: string): CanadianBank | undefined => {
  const normalized = institutionNumber?.padStart(3, '0');
  return BANK_BY_INSTITUTION[normalized];
};

/**
 * Get bank name by institution number
 * @param institutionNumber - 3-digit institution number
 * @returns Bank name or undefined if not found
 */
export const getBankNameByInstitution = (institutionNumber: string): string | undefined => {
  return getBankByInstitution(institutionNumber)?.name;
};

/**
 * Dropdown options for bank selection including "Other" option
 */
export const BANK_DROPDOWN_OPTIONS = [
  ...CANADIAN_BANKS.map((bank) => ({
    value: bank.institutionNumber,
    label: `${bank.name} (${bank.institutionNumber})`
  })),
  { value: 'OTHER', label: 'Other / Not Listed' }
];

/**
 * Special value for "Other" bank option
 */
export const OTHER_BANK_VALUE = 'OTHER';

/**
 * Check if the selected bank is "Other"
 */
export const isOtherBank = (value: string | undefined): boolean => {
  return value === OTHER_BANK_VALUE;
};

/**
 * Validation constants for banking fields
 */
export const BANKING_VALIDATION = {
  INSTITUTION_NUMBER_LENGTH: 3,
  TRANSIT_NUMBER_LENGTH: 5,
  MIN_ACCOUNT_NUMBER_LENGTH: 7,
  MAX_ACCOUNT_NUMBER_LENGTH: 12
};

/**
 * Validate Canadian institution number format
 * @param institutionNumber - Institution number to validate
 * @returns true if valid format (3 digits)
 */
export const isValidInstitutionNumber = (institutionNumber: string | undefined): boolean => {
  if (!institutionNumber) return false;
  return /^\d{3}$/.test(institutionNumber);
};

/**
 * Validate Canadian transit number format
 * @param transitNumber - Transit number to validate
 * @returns true if valid format (5 digits)
 */
export const isValidTransitNumber = (transitNumber: string | undefined): boolean => {
  if (!transitNumber) return false;
  return /^\d{5}$/.test(transitNumber);
};

/**
 * Validate Canadian account number format
 * @param accountNumber - Account number to validate
 * @returns true if valid format (7-12 digits)
 */
export const isValidAccountNumber = (accountNumber: string | undefined): boolean => {
  if (!accountNumber) return false;
  return /^\d{7,12}$/.test(accountNumber);
};

/**
 * CRA Business Number validation constants
 * Format: 9 digits (e.g., 123456789)
 */
export const CRA_NUMBER_LENGTH = 9;

/**
 * Quebec Enterprise Number (NEQ) validation constants
 * Format: 10 digits (e.g., 1234567890)
 */
export const QUEBEC_NUMBER_LENGTH = 10;

/**
 * Validate CRA Business Number format
 * @param craNumber - CRA Business Number to validate
 * @returns true if valid format (9 digits)
 */
export const isValidCRANumber = (craNumber: string | undefined): boolean => {
  if (!craNumber) return false;
  return /^\d{9}$/.test(craNumber);
};

/**
 * Validate Quebec Enterprise Number (NEQ) format
 * @param quebecNumber - Quebec Enterprise Number to validate
 * @returns true if valid format (10 digits)
 */
export const isValidQuebecNumber = (quebecNumber: string | undefined): boolean => {
  if (!quebecNumber) return false;
  return /^\d{10}$/.test(quebecNumber);
};
